<script setup lang="ts">
import { useSlots, type PropType } from 'vue';
import { RouterLink, type RouteLocationRaw } from 'vue-router';
import GaiaText from '../../base/GaiaText.vue';
import GaiaTooltip from '../../base/GaiaTooltip.vue';
import { gaiaAccent, gaiaFontSize, gaiaFontWeight, gaiaNeutral } from '../../themes/tokens';

const props = defineProps({
  /** Where it sits, e.g. "Vault › Commercial". */
  location: {
    type: String,
    default: undefined,
  },
  /** The contract it landed in. */
  contractName: {
    type: String,
    required: true,
  },
  to: {
    type: [String, Object] as PropType<RouteLocationRaw>,
    required: true,
  },
});

// `#details`: more about the contract, shown on the link's hover.
const slots = useSlots();

const surface = gaiaNeutral.surfaceSoft;
const border = gaiaNeutral.borderCard;
const linkColor = gaiaAccent.base;
const linkHover = gaiaAccent.strong;
const linkSize = gaiaFontSize.row;
const linkWeight = gaiaFontWeight.semibold;
</script>

<template>
  <!-- Where an intake file landed (Contracts Overview mock, the box at the top
       of the field column): where it sits, and a way into the contract. -->
  <div class="gaia-intake-landing">
    <gaia-text v-if="props.location" tag="div" size="nano" tone="tertiary">
      {{ props.location }}
    </gaia-text>
    <gaia-tooltip :disabled="!slots.details" placement="bottom-start">
      <template #trigger>
        <router-link :to="props.to" class="link">{{ props.contractName }} ↗</router-link>
      </template>
      <slot name="details" />
    </gaia-tooltip>
  </div>
</template>

<style scoped>
.gaia-intake-landing {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 14px;
  background: v-bind(surface);
  border: 1px solid v-bind(border);
  border-radius: 10px;
}

.link {
  font-size: v-bind(linkSize);
  font-weight: v-bind(linkWeight);
  line-height: 1.4;
  color: v-bind(linkColor);
  text-underline-offset: 3px;
  text-wrap: pretty;
}

.link:hover {
  color: v-bind(linkHover);
}
</style>
